<template>
    <div id="scroll-anchor">
        <div class="sm:hidden flex justify-center mt-4 mb-2">
            <div class="btn square" @click="mobileFiltersVisible = true">
                <filter-icon class="mr-2" />
                Show Filters
            </div>
        </div>
        <div class="flex flex-col md:flex-row md:space-x-6 pb-16">
            <div class="w-full fixed inset-0 z-20 overflow-scroll mt-24 sm:mt-0 bg-white p-5  sm:p-0 sm:block sm:relative sm:overflow-auto md:w-3/10 lg:w-2/10"
                v-bind:class="{ hidden: !mobileFiltersVisible }">
                <div class="mb-20 sm:mb-0">
                    <h4 class="sm:hidden mb-4 font-bold text-lg">BRICK LIBRARY FILTERS</h4>
                    <u-search v-model="search"></u-search>
                    <slot name="pre-filter"></slot>
                    <u-filter v-model="filtered" :filters="filters" :labels="filterLabels" :components="filterComponents" :toggles="filterToggles"></u-filter>
                </div>
                <div class="h-20 fixed bottom-0 left-0 right-0 sm:hidden bg-white border-t flex justify-center items-center">
                    <button @click="mobileFiltersVisible = false" class="btn default grey-primary">Close Filters</button>
                </div>
            </div>
            <div class="w-full md:w-7/10 lg:w-8/10">
                <div class="flex items-center justify-between my-2 md:mt-0 flex-col gap-3 sm:flex-row sm:gap-0">
                    <u-sort :options="sortOptions" v-model="sort" @sort="reload"></u-sort>
                    <div class="flex items-center sm:ml-auto">
                        <u-meta v-if="pagination && pagination.meta && 1 !== pagination.meta.total" class="ml-4" :pagination="pagination" />
                        <u-type class="ml-4 sm:mb-0" v-model="type" />
                    </div>
                </div>
                <div v-if="loading" class="relative w-full h-full">
                    <div class="absolute w-full h-full bg-white top-0 left-0">
                        <div class="flex flex-col items-center justify-center h-56">
                            <loader class="w-6 h-6 animate-spin"></loader>
                            <p class="text-md text-grey-900 mt-3">Loading Bricks...</p>
                        </div>
                    </div>
                </div>
                <div class="relative">
                    <u-results ref="results" class="z-10 results" :class="{ 'results-loading': loading }" :search="search" :items="items" :suggestions="suggestions" :type="type" :pagination="pagination" :loading="!! loading"></u-results>
                    <u-paginator class="mt-8 px-4" v-model="paginator" :pagination="pagination" @paginate="reload" @focus="focus" v-show="! loading"></u-paginator>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import USort from './BrickLibrary/Sort.vue';
    import UType from './BrickLibrary/Type.vue';
    import UMeta from './BrickLibrary/Meta.vue';
    import USearch from './BrickLibrary/Search.vue';
    import UFilter from './BrickLibrary/Filter.vue';
    import UResults from './BrickLibrary/Results.vue';
    import UPaginator from './BrickLibrary/Paginator.vue';
    import Loader from '../components/Icons/Loader.vue'

    import LibraryDataMapper from '../support/LibraryDataMapper.js';
    import Storage from '../support/Storage.js';

    import { debounce } from 'lodash-es';
    import { merge } from 'lodash-es';
    import { uniqueId } from 'lodash-es';
    import { get } from 'lodash-es';
    import { mapValues } from 'lodash-es';

    export default {
        components: {
            Loader,
            USort,
            UType,
            UMeta,
            USearch,
            UFilter,
            UResults,
            UPaginator,
            FilterIcon: () => import('./Icons/FilterIcon.vue')
        },

        props: {
            clearance: {
                type: Boolean,
                required: false,
                default: false,
            },

            reclaimed: {
                type: Boolean,
                required: false,
                default: false,
            },

            filters: {
                type: Object,
                required: true,
            },

            filterAppends: {
                type: Object,
                required: false,
                default() {
                    return {};
                },
            },

            filterLabels: {
                type: Object,
                required: false,
            },

            filterComponents: {
                type: Array,
                required: false,
            },

            filterToggles: {
                type: Array,
                required: false,
            },
        },

        data() {
            const storage = new Storage('brick-library');

            return {
                storage,
                items: [],
                suggestions: [],
                sort: null,
                type: storage.get('type', 'compact'),
                search: null,
                paginator: null,
                pagination: null,
                filtered: null,
                loading: false,

                mobileFiltersVisible: false
            };
        },

        mounted() {
            this.dataMapper = new LibraryDataMapper(this.$router, this.$data, this.reload.bind(this), this.clearance);
            this.dataMapper.boot();
        },

        computed: {
            endpoint() {
                if (this.clearance) {
                    return '/api/cortex/bricks/clearance';
                }

                if (this.reclaimed) {
                    return '/api/cortex/bricks/reclaimed'
                }

                return '/api/cortex/bricks';
            },

            sortOptions() {
                if (this.clearance) {
                    return ['Price', 'Name', 'SKU'];
                }

                return ['Name', 'SKU'];
            },
        },

        watch: {
            type() {
                this.storage.set('type', this.type);
            },
        },

        methods: {
            focus(index) {
                // This is the only decent way to trigger a
                // focus on the next results item[] load
                this.$refs.results.focus(index);
            },

            reload: debounce(function () {
                this.load();
            }, 250),

            async load() {
                const loading = this.loading = uniqueId('loading');

                const perPage = this.paginator.perPage;
                const params = mapValues(merge({}, this.$route.query, { per_page: perPage }, this.filterAppends), option => `${option}`.replace(/\+/g, ' '));

                const defaultOption = this.clearance ? 'price' : 'name';
                let sort = this.sort.by?.toLowerCase() || defaultOption;
                if ('desc' === this.sort?.direction?.toLowerCase()) {
                    sort = `-${sort}`;
                }
                params.sort = sort;

                try {
                    // First, get the list of all bricks.
                    const bricksResponse = await axios.get(this.endpoint, { params })

                    // We've requeried since this request was sent.
                    if (loading !== this.loading) { return; }

                    const bricksData = bricksResponse.data;
                    this.suggestions = bricksData.suggestions;
                    this.pagination = bricksData.pagination;
                    this.items = bricksData.data;

                    // Second, get the SKUs that are currently in the basket
                    const basketResponse = await axios.get('/api/basket')
                    const basketItems = basketResponse.data.items;
                    const skuArray = basketItems.map(item => item.brick?.sku).filter(Boolean);
                    const updatedItemArray = this.items.map(item => ({
                        ...item,
                        skuInBasket: skuArray.includes(item.sku)
                    }));
                    this.items = updatedItemArray;

                } catch (error) {
                    console.error('Something went wrong while fetching the data', error);

                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .results {
        transition: opacity .15s;
        opacity: 1;
    }

    .results-loading {
        opacity: .6;
    }

    .bg-loading {
        background: #222b2f99;
    }
</style>
